import { useState } from 'react';
import { CheckIcon, SearchIcon } from 'lucide-react';
import { Tabs } from '../../ui/Tabs';
import { EmptyState } from '../../ui/EmptyState';
import { NumberInput } from '../../ui/NumberInput';
import { Spinner } from '../../../ui/Spinner';
import { btnDestructive, btnGhost, btnPrimary, inputCls, listBox } from '../../ui/styles';
import { money } from '../../../core/format/currency';
import { dayShort, today } from '../../../core/format/dates';
import { COUNT_INTERVAL_DAYS, locationsDue } from '../../../core/rules/stockCount';
import { locationLabel } from '../../../core/rules/stockMovements';
import { stockLocations, type StockLocation } from '../../../core/types/stock';
import { useStockCount } from './useStockCount';
import type { StockControl } from '../useStock';
const locationTabs = stockLocations.map(l => ({
  key: l,
  label: locationLabel[l]
}));

/**
 * Tabblad Tellen: de wekelijkse telling per plek. Vul in wat je ziet; "Klopt" neemt de verwachte
 * stand over. Alles wat je invult blijft op dit apparaat staan tot je opslaat.
 */
export function StockCount({
  stock
}: {
  stock: StockControl;
}) {
  // Begin bij de plek die aan de beurt is.
  const [location, setLocation] = useState<StockLocation>(() => locationsDue(stock.items ?? [], stock.lastCountAt, today())[0] ?? 'salon');
  const c = useStockCount(stock, location);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const last = stock.lastCountAt[location];
  const counted = c.lines.length;
  return <div data-id="element-4563" data-mp="1funwxu9qqlpf">
      <div className="mb-4 flex flex-wrap items-center gap-3" data-id="element-4564" data-mp="1fu5uuh0edd9l">
        <Tabs variant="pill" label="Plek" tabs={locationTabs} value={location} onChange={setLocation} data-id="element-4565" data-mp="1fws2shr79usc" />
        <p className={`text-[13px] ${c.due.due ? 'text-clay' : 'text-muted'}`} data-id="element-4566" data-mp="164mb794v1fdo">
          {last ? `Laatst helemaal geteld: ${dayShort(last.slice(0, 10))} (${c.due.daysSince === 0 ? 'vandaag' : `${c.due.daysSince} ${c.due.daysSince === 1 ? 'dag' : 'dagen'} geleden`})` : 'Nog nooit helemaal geteld'}
          {c.due.due && ` · elke ${COUNT_INTERVAL_DAYS} dagen tellen houdt de kassa en webshop eerlijk`}
        </p>
      </div>

      {c.result && <section aria-live="polite" className="mb-5 rounded-lg border border-line bg-white p-4 sm:p-5" data-id="element-4567" data-mp="2mntuacuxqteo">
          <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-4568" data-mp="28v4amf2vq0kq">
            <div data-id="element-4569" data-mp="2cx90r3u4cf2m">
              <h2 className="text-[15px] font-medium text-ink" data-id="element-4570" data-mp="2cwphnjsqfkn0">
                {c.result.count.complete ? `${locationLabel[location]} is helemaal geteld` : `Telling opgeslagen: ${c.result.count.counted} geteld`}
              </h2>
              <p className="mt-1 text-[13px] text-muted" data-id="element-4571" data-mp="1pymb3bhsgwdx">
                {c.result.count.differences === 0 ? 'Alles klopte.' : `${c.result.count.differences} ${c.result.count.differences === 1 ? 'verschil' : 'verschillen'}, samen ${c.result.count.netQty > 0 ? '+' : ''}${c.result.count.netQty} stuks (${money(c.result.count.netCostCents)} tegen inkoopprijs).`}
                {!c.result.count.complete && c.result.conflicts.length === 0 && ' Niet alles is geteld, dus dit telt nog niet als de wekelijkse telling.'}
              </p>
            </div>
            <button type="button" onClick={c.dismissResult} className={btnGhost} data-id="element-4572" data-mp="1mgf05wvdl5ps">
              Sluiten
            </button>
          </div>
          {c.result.conflicts.length > 0 && <div className="mt-3 border-t border-line pt-3" data-id="element-4573" data-mp="1hnvox6k5umo5">
              <p className="text-[13px] text-clay" data-id="element-4574" data-mp="1wd9xicfwx02h">Intussen verkocht of gewijzigd, dus niet geboekt. Tel deze opnieuw:</p>
              <ul className="mt-1 text-[13px] text-ink" data-id="element-4575" data-mp="1rf8d9o6r2upf">
                {c.result.conflicts.map(x => <li key={x.itemId} data-id="element-4576" data-mp="25d6q2p5faj2t">
                    {x.name}: stond op {x.expected ?? '—'}, nu {x.current ?? '—'}
                  </li>)}
              </ul>
            </div>}
        </section>}

      {c.all.length === 0 ? <EmptyState title={`Op de plek ${locationLabel[location].toLowerCase()} wordt niets bijgehouden`} text="Zet eerst een getal bij een artikel (Overzicht › Tellen of Inkoop ontvangen), dan verschijnt het hier." data-id="element-4577" data-mp="63gr9ztxyd5w" /> : <>
          <div className="mb-3 flex flex-wrap items-center gap-3" data-id="element-4578" data-mp="hh0dwalzltev">
            <label className="relative block w-full sm:w-72" data-id="element-4579" data-mp="hfpa4asjklnr">
              <span className="sr-only" data-id="element-4580" data-mp="mfa4s90oi7uo">Zoeken</span>
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" strokeWidth={1.75} aria-hidden="true" data-id="element-4581" data-mp="3kyii882jwiaj" />
              <input type="search" value={c.query} onChange={e => c.setQuery(e.target.value)} placeholder="Naam of merk" className={`${inputCls} pl-9`} data-id="element-4582" data-mp="3pxldj0sqba5b" />
            </label>
            <label className="flex items-center gap-2 text-[13px] text-ink" data-id="element-4583" data-mp="3ishc3sv4u48">
              <input type="checkbox" checked={c.onlyOpen} onChange={e => c.setOnlyOpen(e.target.checked)} className="h-4 w-4 accent-clay" data-id="element-4584" data-mp="32897g9sp7i78" />
              Alleen nog niet geteld
            </label>
            <p className="text-[13px] tabular-nums text-muted sm:ml-auto" aria-live="polite" data-id="element-4585" data-mp="2znunw2gku8bs">
              {counted} van {c.all.length} geteld
              {c.summary.differences > 0 && ` · ${c.summary.differences} ${c.summary.differences === 1 ? 'verschil' : 'verschillen'}`}
            </p>
          </div>

          <div className="mb-3 h-1 overflow-hidden rounded-full bg-line" aria-hidden="true" data-id="element-4586" data-mp="2kqtuzvmng7bq">
            <div className="h-full rounded-full bg-sage transition-[width] duration-200 ease-soft" style={{
          width: `${counted / c.all.length * 100}%`
        }} data-id="element-4587" data-mp="2gop434nl5oss" />
          </div>

          <ul className={listBox} aria-label={`Telling ${locationLabel[location]}`} data-id="element-4588" data-mp="26ynicjipgih1">
            {c.visible.map(item => {
          const d = c.draft[item.id];
          const expected = d?.expected ?? item.levels[location];
          const diff = d ? d.counted - (expected ?? 0) : null;
          return <li key={item.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_80px_auto_64px] sm:px-5" data-id="element-4589" data-mp="1symh32yj3911">
                  <span className="min-w-0" data-id="element-4590" data-mp="1nclwrz5ep294">
                    <span className="block truncate text-[14px] text-ink" data-id="element-4591" data-mp="1ndn884qc3qrd">{item.name}</span>
                    <span className="block truncate text-[12px] text-muted" data-id="element-4592" data-mp="19ee4w5n88r4t">
                      {item.brand ?? (item.kind === 'verbruik' ? 'Verbruik' : 'Product')}
                      <span className="sm:hidden" data-id="element-4593" data-mp="22203t0lif7lc"> · verwacht {expected ?? '—'}</span>
                    </span>
                  </span>
                  <span className="hidden text-right text-[13px] tabular-nums text-muted sm:block" data-id="element-4594" data-mp="26220swgavcua">verwacht {expected ?? '—'}</span>
                  <span className="flex items-center gap-2" data-id="element-4595" data-mp="1s9ue5xiqd29b">
                    <label className="block" data-id="element-4596" data-mp="1x9qkw4itb7sk">
                      <span className="sr-only" data-id="element-4597" data-mp="2l0m96ro27kv2">Geteld: {item.name}</span>
                      <NumberInput allowEmpty value={d?.counted} onChange={v => c.setCounted(item.id, v)} min={0} placeholder="—" className="w-20 text-right" data-id="element-4598" data-mp="2kzb6ywa9il62" />
                    </label>
                    {!d && expected !== null && expected >= 0 && <button type="button" onClick={() => c.setCounted(item.id, expected)} className="inline-flex h-9 items-center gap-1 rounded-md px-2 text-[13px] text-muted transition-colors duration-150 ease-soft hover:bg-sand hover:text-ink" aria-label={`${item.name}: klopt, ${expected}`} data-id="element-4599" data-mp="2v5cdv5aul32z">
                        <CheckIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4600" data-mp="2c704big0xr6v" />
                        Klopt
                      </button>}
                  </span>
                  <span className={`col-span-2 text-right text-[13px] tabular-nums sm:col-span-1 ${diff ? 'text-clay' : 'text-muted'}`} data-id="element-4601" data-mp="2l5lk55u42s3y">
                    {diff === null ? '' : diff === 0 ? 'klopt' : `${diff > 0 ? '+' : ''}${diff}`}
                  </span>
                </li>;
        })}
          </ul>

          <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-4602" data-mp="1uy7aif45pqku">
            <button type="button" onClick={() => void c.save()} disabled={counted === 0 || c.submitting} aria-busy={c.submitting} className={btnPrimary} data-id="element-4603" data-mp="1yyzkxborm1ip">
              {c.submitting && <Spinner data-id="element-4604" data-mp="28nhkjplb62kr" />}
              {counted === 0 ? 'Telling opslaan' : `Telling opslaan (${counted})`}
            </button>
            {counted > 0 && !c.submitting && (confirmDiscard ? <span className="flex items-center gap-2 text-[13px] text-ink" data-id="element-4605" data-mp="2ikvztlvhwlbh">
                  {counted} ingevulde {counted === 1 ? 'regel' : 'regels'} wissen?
                  <button type="button" onClick={() => {
            c.discard();
            setConfirmDiscard(false);
          }} className={btnDestructive} data-id="element-4606" data-mp="2rmdaupu1pwtb">
                    Wissen
                  </button>
                  <button type="button" onClick={() => setConfirmDiscard(false)} className={btnGhost} data-id="element-4607" data-mp="ppct00ey5kae">
                    Houden
                  </button>
                </span> : <button type="button" onClick={() => setConfirmDiscard(true)} className={btnGhost} data-id="element-4608" data-mp="fyi932p6aws4">
                  Opnieuw beginnen
                </button>)}
            <p className="text-[12px] text-muted" data-id="element-4609" data-mp="18nxl9vskqvpm">
              {counted < c.all.length ? 'Je kunt tussendoor opslaan. Wat je invult blijft op dit apparaat bewaard. Pas als alles geteld is, telt het als de wekelijkse telling.' : 'Alles ingevuld: na opslaan staat de wekelijkse telling voor deze plek weer op groen.'}
            </p>
          </div>
          {c.error && <p role="alert" className="mt-2 text-[13px] text-clay" data-id="element-4610" data-mp="3jfqprj85zql9">
              {c.error}
            </p>}
        </>}
    </div>;
}